import { DayFlag, SelectionState, UI } from 'react-day-picker';
import type { ClassNames } from 'react-day-picker';

import { cva } from 'class-variance-authority';

/**
 * Date picker class recipes — the field, and the portalled calendar.
 *
 * Like `Button.recipe`, `TextInput.recipe` and `TextArea.recipe`, these contain ZERO `dark:`
 * variants: colour comes entirely from the `--ds-datepicker-*` slots that `.ds-datepicker` sets in
 * `components/styles/design-system.css`. Every one of those slots names a semantic role, so the
 * class needs no dark rule at all.
 *
 * Every state selector on the FIELD reads Base UI's `Field.Root` attributes through the
 * `group/ti` marker (`data-focused`, `data-filled`, `data-invalid`, `data-disabled`); every state
 * selector on a GRID CELL reads `react-day-picker`'s attributes through the `group/day` marker
 * (`data-selected`, `data-today`, `data-outside`, `data-disabled`). There is no React state behind
 * either set, which also means the states are reachable from a story by rendering.
 *
 * Both sets are MUTUALLY EXCLUSIVE rather than relying on utility sort order, the same discipline
 * every other recipe in this layer uses. Precedence, highest first:
 *
 *   field:  disabled → invalid → focused → hover → resting
 *   cell:   disabled → selected → today → outside → hover → resting
 *
 * ── Why none of this imports `TextInput.recipe` ────────────────────────────────────────────────
 *
 * The field geometry below is a near-copy of `textInputBoxRecipe`'s, and importing it would be
 * less code. It is restated anyway, for the two reasons `TextArea.recipe` sets out at length: the
 * `ds-input` class string would drag the first-generation palette basis back in, and each
 * component owning its own slot namespace is what stops a change made for one breaking another.
 *
 * This is the THIRD hand-written copy of that geometry, which is one more chance to drift than the
 * decision was first taken against. `DatePicker.test.tsx` therefore pins it the same way
 * `TextArea.test.tsx` does — importing both recipes and comparing the radius, border width and
 * min-height they emit for every `position`. The slot harness cannot do that job: `parseSlotClasses`
 * reads `--ds-*` declarations inside `.ds-*` CSS rules, and radius and min-height are Tailwind
 * literals in a TypeScript file.
 *
 * ── THE CALENDAR HALF HAS NO DESIGN BEHIND IT ──────────────────────────────────────────────────
 *
 * There is no date picker in the Design System 2.0 library, in any state. The field restates the
 * field family's numbers and is as settled as they are; every number below the field — the 40px
 * cell, the 32px nav button, the 12px weekday, the 20px popup radius — was chosen here and is on
 * the design review's agenda along with the six cell colours. See the `.ds-datepicker` block.
 */

/**
 * Marker + layout for `Field.Root`.
 *
 * The `.ds-datepicker` slot class lives HERE rather than on the bordered box, for the reason
 * `textInputRootRecipe` documents: custom properties cascade, so everything inside inherits them,
 * and anything that later renders as a SIBLING of the box would otherwise fall outside their scope
 * and resolve to inherited colour instead of failing visibly.
 *
 * The group marker is `group/ti` rather than a name of its own, shared with `TextInput` and
 * `TextArea` on purpose: the three are alternative fields, never nested one inside another, so
 * there is no ambiguity to resolve — and sharing it means a stacked group's state selectors read
 * the same whichever field type is in it.
 */
export const datePickerRootRecipe = cva(['group/ti ds-datepicker', 'relative flex w-full flex-col']);

/**
 * The bordered box.
 *
 * Holds no padding of its own — all of it is on the `<input>`, exactly as on `TextInput` and
 * `TextArea` and for the same reason: padding the box would leave the input a strip inside a
 * larger box, so clicking near the edges would do nothing and the caret would land in the wrong
 * place. The only exception is the bottom inset when helper text is present, which the control can
 * no longer pay for because the helper sits below it.
 *
 * `min-h-[60px]` is `TextInput`'s floor rather than `TextArea`'s 100px, because this is a
 * single-line field: 1 + 28 + 20 + 10 + 1. The pin test asserts the match.
 *
 * ── Why the stroke is drawn with shadows, not the border ───────────────────────────────────────
 *
 * `border-width` NEVER changes here, in any state, for the reason `textInputBoxRecipe` spells out:
 * a border is layout, so thickening it on focus moves the padding box and shifts the absolutely
 * positioned label, the value text and — unique to this field — the calendar button pinned to the
 * right gutter. So the border stays at its resting 1px and everything state-dependent is painted
 * as an inset `box-shadow`, which occupies no layout space:
 *
 *   inset 0 0 0 0.5px  --ds-datepicker-stroke-extra   the half-pixel that makes the stroke 1.5px
 *   inset 0 0 0 2.5px  --ds-datepicker-ring-current   the lighter tint, in the 2px inside that
 *
 * Earlier shadows paint over later ones, so reading inward that is 1px of border, 0.5px of the
 * same colour, then 2px of tint. Both slots default to `transparent`, which is what lets the
 * shadow rule be unconditional — only the slots are switched per state.
 *
 * ── The focus outline, and why the box's is keyed to the INPUT ────────────────────────────────
 *
 * `TextArea` draws an offset outline around its whole box, and so does this field. The two are the
 * role-based field primitives designed to stack in one joined group, so a group whose fields
 * answered a Tab differently would read as a bug in exactly the composition they exist for.
 *
 * It hangs off `has-[...:focus-visible]` rather than Button's `focus-visible:` prefix for the
 * reason `textAreaBoxRecipe` spells out: the element carrying it is a `<div>` and cannot be
 * focused, and `group-data-[focused]` — what the rest of this recipe reads — is set by Base UI for
 * ANY focus, so using that here would draw a 2px outline on every mouse click into the field.
 *
 * The one difference from the sibling field is the selector's ARGUMENT: `input:focus-visible`
 * rather than anything focusable. This field has two focusable things in it, and a box-level
 * outline that answered for the calendar button as well would tell the user nothing about which
 * one they had reached — and would draw a second ring around the button's own. So the box answers
 * for the input and the button answers for itself (see `datePickerTriggerRecipe`).
 *
 * The inset tint is kept alongside the outline, as on `TextArea`, and the two are not redundant in
 * role: the tint is a low-contrast wash that would not satisfy 1.4.11 at any proportion that still
 * read as a halo, and the outline is the indicator that actually clears 3:1.
 */
export const datePickerBoxRecipe = cva(
  [
    'relative flex w-full flex-col gap-[4px]',
    'min-h-[60px]',
    'border-solid border-[length:var(--ds-datepicker-border-width)] border-[var(--ds-datepicker-border)]',
    'bg-[var(--ds-datepicker-bg)]',
    'cursor-text',
    'transition-[border-color,background-color,box-shadow,outline-color] duration-150 motion-reduce:transition-none',

    // Hover is excluded from every other state so it can never fight them.
    'group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-datepicker-border-hover)]',

    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-datepicker-border-focus)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-datepicker-border-invalid)]',
    'group-data-[disabled]/ti:border-[var(--ds-datepicker-border-disabled)]',
    'group-data-[disabled]/ti:bg-[var(--ds-datepicker-bg-disabled)]',
    'group-data-[disabled]/ti:cursor-not-allowed',

    // Both shadow layers off by default, so the rule below can be unconditional.
    '[--ds-datepicker-stroke-extra:transparent] [--ds-datepicker-ring-current:transparent]',
    'shadow-[inset_0_0_0_0.5px_var(--ds-datepicker-stroke-extra),inset_0_0_0_2.5px_var(--ds-datepicker-ring-current)]',

    // The half-pixel thickening, in the same colour as whichever border is showing. Focus, error
    // and disabled each reach 1.5px; hover and resting stay at 1px.
    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:[--ds-datepicker-stroke-extra:var(--ds-datepicker-border-focus)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:[--ds-datepicker-stroke-extra:var(--ds-datepicker-border-invalid)]',
    'group-data-[disabled]/ti:[--ds-datepicker-stroke-extra:var(--ds-datepicker-border-disabled)]',

    // An errored field keeps its red border when focused, so a blue tint inside it would read as a
    // bug. Remapping the slot keeps the line below a single read, as the border rules are.
    'group-data-[invalid]/ti:[--ds-datepicker-ring:var(--ds-datepicker-ring-invalid)]',
    'group-data-[focused]/ti:[--ds-datepicker-ring-current:var(--ds-datepicker-ring)]',

    /**
     * The focus indicator proper — see the long note above for why it is keyed to the input.
     *
     * It names `--border-focus` directly rather than through a slot, which is what `Button.recipe`
     * and `textAreaBoxRecipe` do with the same treatment: the outline is not part of the
     * component's palette, it is the one focus colour the whole system shares.
     *
     * `outline-transparent` on the base so the colour has something to transition FROM; without it
     * the outline appears at full strength on the first frame.
     */
    'outline-transparent',
    'has-[input:focus-visible]:outline-2 has-[input:focus-visible]:outline-offset-2 has-[input:focus-visible]:outline-[var(--border-focus)]',
  ],
  {
    variants: {
      /**
       * Figma's `Corner Radius` axis, pinned to `TextInput`'s values by the test. The design draws
       * three of these four; `middle` has no cell and is extrapolated — see `TextInputPosition`,
       * which this component imports rather than re-declaring.
       */
      position: {
        standalone: 'rounded-[16px]',
        first: 'rounded-t-[16px] rounded-b-none',
        middle: 'rounded-none',
        last: 'rounded-b-[16px] rounded-t-none',
      },
      /** The bottom inset, which moves between the box and the control when a helper row exists. */
      hasHelper: {
        true: 'pb-[10px]',
        false: 'pb-0',
      },
    },
    defaultVariants: { position: 'standalone', hasHelper: false },
  }
);

/**
 * The floating label.
 *
 * Mechanically identical to `textInputLabelRecipe` and `textAreaLabelRecipe` — same 19px resting
 * offset, same 10px shrunk offset, same 11/14 scale, same composited `translate`/`scale`
 * animation — so the three field types stacked in one group have their labels on the same lines.
 * That alignment is the reason to copy the numbers rather than re-derive them.
 *
 * `max-w` clears the calendar button as well as the two 24px gutters, which is why it subtracts
 * more than the sibling fields do.
 *
 * Deliberately NOT `pointer-events-none`. This is a real `<label>` wired to the control by `Field`,
 * so a click on it focuses the input natively; making it click-through would instead drop clicks on
 * the part of it that overhangs the control.
 */
export const datePickerLabelRecipe = cva([
  'absolute top-0 left-[24px] origin-left cursor-text select-none',
  'font-sans text-body-md text-[var(--ds-datepicker-label)]',
  'max-w-[calc(100%_-_74px)] truncate',
  'translate-y-[19px] scale-100',

  // `translate` and `scale`, not `transform`: Tailwind v4's utilities set the standalone CSS
  // properties of those names, so a transition on `transform` would match nothing and the label
  // would jump to its shrunk position before animating its size. `scale-100` is stated on the base
  // because interpolating from `scale: none` is not reliably animatable.
  'transition-[translate,scale,color] duration-150 motion-reduce:transition-none',

  // 11/14 — the shrunk size as a ratio of the resting one, since the font-size itself never changes.
  'group-data-[filled]/ti:translate-y-[10px] group-data-[filled]/ti:scale-[0.785714]',
  'group-data-[focused]/ti:translate-y-[10px] group-data-[focused]/ti:scale-[0.785714]',

  /**
   * And whenever a placeholder is showing, which is the third way the value line gets occupied.
   *
   * Without this the resting label sits exactly where the placeholder draws and the two overlap
   * into an unreadable stack — the label is centred on the value line when the field is empty, and
   * a placeholder is by definition only visible when the field is empty. The design's answer is
   * that the placeholder takes the value's place, in `foreground/secondary`, which only works if
   * the label has already moved out of the way.
   *
   * `:placeholder-shown` is the exact condition and nothing narrower is needed: it matches only
   * when the element HAS a placeholder and is empty, so a field without one never matches and its
   * label still rests centred. Reading it off the control through `group-has-` rather than adding
   * a prop keeps the component's state observed from the DOM, like every other state here.
   */
  'group-has-[:placeholder-shown]/ti:translate-y-[10px] group-has-[:placeholder-shown]/ti:scale-[0.785714]',
  'group-data-[disabled]/ti:cursor-not-allowed group-data-[disabled]/ti:text-[var(--ds-datepicker-label-disabled)]',
]);

/**
 * The `<input>`, which is also most of the field's hit area.
 *
 * `pt-[28px]` puts the value's 20px line box where `TextInput` and `TextArea` draw theirs, so the
 * three agree when stacked. Everything above that line is still part of the control rather than
 * dead space on the box.
 *
 * `pr-[50px]` is `textInputControlRecipe`'s trailing-icon inset — 24px gutter, 16px of glyph, a
 * 10px gap. The calendar BUTTON is wider than the glyph it contains (see below), but its left edge
 * still lands 44px from the right, inside that 50px, so the typed text never runs under it.
 *
 * Preflight is deliberately absent from the token layer, so the browser's own input styling is
 * still in play and every reset is spelled out rather than assumed.
 */
export const datePickerControlRecipe = cva(
  [
    'm-0 block w-full appearance-none border-0 bg-transparent',
    'font-sans text-body-md text-[var(--ds-datepicker-value)]',
    'outline-none',
    'pt-[28px] pr-[50px] pl-[24px]',
    'cursor-text',
    'placeholder:text-[var(--ds-datepicker-placeholder)]',
    'disabled:cursor-not-allowed disabled:text-[var(--ds-datepicker-value-disabled)]',
    // Edge and Chrome add their own clear affordance, which would sit beside the calendar button.
    '[&::-ms-clear]:hidden',
  ],
  {
    variants: {
      /** When helper text follows, the bottom inset moves to the box — see `datePickerBoxRecipe`. */
      hasHelper: {
        true: 'pb-0',
        false: 'pb-[10px]',
      },
    },
    defaultVariants: { hasHelper: false },
  }
);

/**
 * The button that opens the calendar.
 *
 * ── Why it is 24px and not the 16px the field family's icon slot uses ─────────────────────────
 *
 * `textInputAffixRecipe` draws a 16px cell at `right-[24px] top-[21px]`. That is the right size
 * for a GLYPH, and `TextInput` uses it for one; here the cell is a real button, and a 16×16 target
 * is under WCAG 2.5.8's 24×24 minimum. So the button is 24×24 centred on exactly the point the
 * affix slot would have put the glyph — `right-[20px] top-[17px]` is the same centre, four pixels
 * out in each direction — and the 16px glyph inside it is unchanged. Nothing moves relative to the
 * sibling fields; the target simply grew into the gutter, which is empty.
 *
 * `focus-visible:` rather than the box's `has-[input:focus-visible]`, because this element CAN be
 * focused, and because the whole point of putting an outline here is to distinguish reaching the
 * button from reaching the input — which is also why the box's own outline deliberately ignores
 * this element rather than lighting up around it. Button's three utilities verbatim, which is the
 * system's one focus treatment.
 */
export const datePickerTriggerRecipe = cva([
  'absolute top-[17px] right-[20px] inline-flex size-[24px] items-center justify-center',
  'm-0 cursor-pointer appearance-none rounded-full border-0 bg-transparent p-0',
  'text-[var(--ds-datepicker-trigger-fg)]',
  'transition-[color] duration-150 motion-reduce:transition-none',
  'disabled:cursor-not-allowed disabled:text-[var(--ds-datepicker-trigger-fg-disabled)]',
  'outline-transparent',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]',
  '[&_svg]:size-[16px]',
]);

/**
 * Helper / error text, rendered inside the border as the other two fields do.
 *
 * Inside rather than below, and that placement is what makes the stacked group work:
 * `TextInputGroup` collapses the borders of adjacent CHILDREN, so anything rendered outside the box
 * would sit between two borders that are supposed to be one line.
 */
export const datePickerHelperRecipe = cva([
  'flex items-center',
  // `m-0` is load-bearing rather than tidiness: `Field.Description` renders a `<p>`, and with
  // Preflight absent it keeps the browser's default `margin: 1em 0`, which the `<div>` that
  // `Field.Error` renders does not have — so the box would change height with the state.
  'm-0',
  'px-[24px]',
  'font-sans text-body-sm text-[var(--ds-datepicker-helper)]',
  'group-data-[invalid]/ti:text-[var(--ds-datepicker-helper-invalid)]',
]);

/** The positioner. `z-[1400]` is the layer `Select` puts its menu on, above every MUI surface. */
export const datePickerPositionerRecipe = cva(['z-[1400]']);

/**
 * The calendar popup.
 *
 * `ds-datepicker` is repeated here by the component rather than inherited: the popup is portalled
 * into `<body>`, so it has none of the field's ancestors and would otherwise resolve every slot to
 * nothing. The theme class is NOT repeated — `DatePicker` puts it on `Popover.Portal`, which is an
 * ancestor of this element, so the ordinary cascade reaches it. `selectMenuRecipe` documents the
 * same pair of facts.
 *
 * `max-h-[var(--available-height)]` plus scrolling keeps the calendar on screen near the bottom of
 * the viewport; Base UI computes the value from the collision boundary.
 */
export const datePickerPopupRecipe = cva([
  'ds-datepicker',
  'box-border flex flex-col',
  'max-h-[var(--available-height)] overflow-y-auto overscroll-contain',
  'p-[16px]',
  'rounded-[20px]',
  'border-solid border-[length:1px] border-[var(--ds-datepicker-popup-border)]',
  'bg-[var(--ds-datepicker-popup-bg)]',
  'outline-none',
]);

/**
 * The `classNames` map handed to `react-day-picker`.
 *
 * A plain object rather than a `cva`, because there is nothing to vary: every key is a fixed class
 * string and the library merges the map over its own defaults. Every visual value the calendar
 * draws is in here, which is what keeps `DatePicker.tsx` free of appearance.
 *
 * ── The keys are v9's, and v8's are silently ignored ──────────────────────────────────────────
 *
 * Version 9 renamed the element keys wholesale: v8's `cell` is v9's `day`, v8's `day` is v9's
 * `day_button`, and the state keys moved from `day_selected`/`day_outside` to `selected`/`outside`.
 * The v8 names still TYPE-CHECK — the library keeps them in a `DeprecatedUI` map so an upgrade
 * reports something useful — so a map written against v8 documentation compiles cleanly and styles
 * nothing. `UI`, `DayFlag` and `SelectionState` are imported as the source of truth rather than
 * spelled as string literals, so a rename in a future major fails the build instead of the eye.
 *
 * ── Why the state classes hang off the CELL and not the button ────────────────────────────────
 *
 * The library puts `data-selected`, `data-today`, `data-outside` and `data-disabled` on the `<td>`
 * and renders the `<button>` inside it. So the `<td>` carries a `group/day` marker and every state
 * rule below is a `group-[...]/day:` variant on the button. Styling the `<td>` directly would work
 * for colour but not for the ring or the hit area, both of which belong to the button.
 */
const navButton = [
  'inline-flex size-[32px] items-center justify-center',
  'm-0 cursor-pointer appearance-none rounded-full border-0 bg-transparent p-0',
  'text-[var(--ds-datepicker-nav-fg)]',
  'transition-[background-color,color] duration-150 motion-reduce:transition-none',
  'hover:not-disabled:bg-[var(--ds-datepicker-nav-bg-hover)]',
  'disabled:cursor-not-allowed disabled:text-[var(--ds-datepicker-nav-fg-disabled)]',
  'outline-transparent',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]',
].join(' ');

export const datePickerCalendarClassNames: Partial<ClassNames> = {
  [UI.Root]: 'flex flex-col',
  [UI.Months]: 'relative flex flex-col',
  [UI.Month]: 'flex flex-col gap-[4px]',

  /**
   * Month name on the left, both chevrons together on the right.
   *
   * The nav has to come out of flow whatever the arrangement, because the library renders it as a
   * sibling of the months rather than inside the caption row. What it must NOT do is span the
   * full width with the caption underneath it: an earlier revision did exactly that, to put one
   * chevron either side of the month name, and axe filed the month heading under `incomplete`
   * with an undeterminable background — a transparent 32px box laid over text is still an
   * overlap, and an undeterminable contrast is not a contrast that has been checked. Pinning the
   * nav to the right removes the overlap rather than suppressing the report.
   */
  [UI.Nav]: 'absolute top-0 right-0 flex h-[32px] items-center gap-[4px]',
  [UI.MonthCaption]: 'flex h-[32px] items-center justify-start pl-[8px]',
  [UI.CaptionLabel]: 'font-sans text-label-lg text-[var(--ds-datepicker-caption-fg)]',

  [UI.ButtonPrevious]: navButton,
  [UI.ButtonNext]: navButton,

  /**
   * The library's chevron is a bare `<polygon>` with no `fill` attribute, which paints BLACK by
   * default rather than inheriting — so `fill-current` is not tidiness, it is the only thing
   * making the glyph follow its button's colour in dark mode. The size utilities override the
   * component's own `width`/`height` attributes, because CSS beats a presentation attribute.
   */
  [UI.Chevron]: 'size-[16px] fill-current',

  [UI.MonthGrid]: 'w-full border-collapse',
  [UI.Weekdays]: '',
  [UI.Weekday]: [
    'size-[40px] p-0 text-center align-middle',
    'font-sans text-label-md text-[var(--ds-datepicker-weekday-fg)]',
  ].join(' '),
  [UI.Weeks]: '',
  [UI.Week]: '',

  /** The `<td>`. Carries the state attributes, so it carries the group marker. */
  [UI.Day]: 'group/day p-0 text-center',

  /**
   * The `<button>` in each cell.
   *
   * ── The hit area, which no design specifies ───────────────────────────────────────────────
   *
   * 40x40, stated here because it has to be stated somewhere and nothing else in the system
   * implies it. This is the densest interactive surface of the five primitives — forty-two
   * targets in a 280px square — so the number is doing real work rather than following a
   * convention. WCAG 2.5.8 asks 24x24 as a minimum and 2.5.5 asks 44x44 as an enhancement; 40
   * clears the requirement comfortably, keeps a seven-column month inside the 280px a popover can
   * take without scrolling at a 320px viewport, and matches the 40px row the platform date
   * pickers this will be compared against use. `DatePicker.stories.tsx` measures it for real
   * rather than trusting the utility.
   *
   * ── The border is always there ────────────────────────────────────────────────────────────
   *
   * `border-transparent` at rest, recoloured for today. A border that appeared only for today
   * would make today's number 1px smaller than every other number in the grid and shift the whole
   * row, which is the same layout-versus-paint trap the field's stroke avoids — here it is
   * cheaper to simply always reserve the pixel.
   */
  [UI.DayButton]: [
    'relative flex size-[40px] items-center justify-center',
    'm-0 cursor-pointer appearance-none rounded-full p-0',
    'border-solid border-[length:var(--ds-datepicker-day-border-width)] border-transparent',
    'bg-[var(--ds-datepicker-day-bg)]',
    'font-sans text-body-sm text-[var(--ds-datepicker-day-fg)]',
    'transition-[background-color,color,border-color] duration-150 motion-reduce:transition-none',

    // Hover: fill only, and excluded from selected and disabled so it can never fight them. It
    // deliberately does NOT recolour the number, which is what lets it coexist with today's and
    // the outside month's foregrounds without a fourth exclusion on each.
    'group-[:hover:not([data-disabled]):not([data-selected])]/day:bg-[var(--ds-datepicker-day-bg-hover)]',

    // Selected beats everything but disabled.
    'group-[[data-selected]:not([data-disabled])]/day:bg-[var(--ds-datepicker-day-bg-selected)]',
    'group-[[data-selected]:not([data-disabled])]/day:text-[var(--ds-datepicker-day-fg-selected)]',

    // Today is a ring plus a recoloured number, suppressed once the day is selected: a blue ring
    // drawn on the solid blue selected fill is invisible, and drawing it anyway would imply a
    // state that is not being shown.
    'group-[[data-today]:not([data-selected]):not([data-disabled])]/day:border-[var(--ds-datepicker-day-border-today)]',
    'group-[[data-today]:not([data-selected]):not([data-disabled])]/day:text-[var(--ds-datepicker-day-fg-today)]',

    // A day from the neighbouring month is still selectable, so it is stepped back rather than
    // washed out. Excluded from today, because "today, in the month you are not looking at" is
    // more useful information than "not this month".
    'group-[[data-outside]:not([data-today]):not([data-selected]):not([data-disabled])]/day:text-[var(--ds-datepicker-day-fg-outside)]',

    'group-data-[disabled]/day:cursor-not-allowed',
    'group-data-[disabled]/day:text-[var(--ds-datepicker-day-fg-disabled)]',

    'outline-transparent',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--border-focus)]',
  ].join(' '),

  /** A hidden day still occupies its cell, so the grid keeps its shape. */
  [DayFlag.hidden]: 'invisible',

  /**
   * Named and left EMPTY on purpose, rather than omitted.
   *
   * The library appends these state classes to the `<td>` as well as setting the matching
   * `data-*` attributes, and its defaults (`rdp-selected`, `rdp-today`, …) would otherwise survive
   * into the markup. They style nothing — this component never imports the library's stylesheet —
   * but a class named after a state that carries no styling is exactly the thing a future reader
   * writes a rule against, and it would then be the one rule in this component not resolved from
   * a role. Every state above is drawn from the `data-*` attribute instead.
   */
  [SelectionState.selected]: '',
  [DayFlag.today]: '',
  [DayFlag.outside]: '',
  [DayFlag.disabled]: '',
  [DayFlag.focused]: '',
};
